import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import evidenceData from '../story/the-missing-hour/evidence_data.json';
import Header from './Header';

const EvidenceDetail = () => {
  const { evidenceId } = useParams();
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  // Transform story evidence data to match our display format
  const transformEvidenceData = (evidence) => {
    return evidence.map((item, index) => {
      // Generate a relevance score based on forensic status and investigation day
      let relevanceScore = 5; // Default score
      if (item.forensicStatus === 'match') relevanceScore = 8;
      else if (item.forensicStatus === 'pending') relevanceScore = 6;
      else if (item.forensicStatus === 'inconclusive') relevanceScore = 4;
      
      // Add priority based on evidence type and status
      let priority = 'medium';
      if (item.evidenceType === 'biological' || item.evidenceType === 'physical') priority = 'high';
      if (item.forensicStatus === 'pending' && item.evidenceType === 'biological') priority = 'urgent';
      
      // Format investigation day for display
      const investigationDate = new Date(2024, 0, 15 + (item.investigationDay - 1));
      const formattedDate = investigationDate.toISOString().split('T')[0];
      
      return {
        id: item.id,
        evidenceNumber: item.id,
        title: item.description,
        evidenceType: item.evidenceType.charAt(0).toUpperCase() + item.evidenceType.slice(1).replace('_', ' '),
        submittedDate: formattedDate,
        submittedTime: "09:00", // Default time since not in original data
        location: item.foundLocation,
        submitterName: "Investigation Team", // Default since not in original data
        status: item.forensicStatus.charAt(0).toUpperCase() + item.forensicStatus.slice(1),
        relevanceScore: relevanceScore,
        priority: priority,
        investigationDay: item.investigationDay,
        originalData: item, // Keep original data for detailed view
        aiAnalysis: {
          evidenceReview: item.notes || "Evidence collected and logged for forensic analysis.",
          storyAlignment: `This evidence was discovered on investigation day ${item.investigationDay} and is currently ${item.forensicStatus}.`,
          investigativeValue: `Evidence type: ${item.evidenceType}. Status: ${item.forensicStatus}.`,
          recommendedActions: ["Forensic analysis", "Documentation", "Chain of custody verification"],
          imageAnalysis: item.photographs ? "Photographs available for analysis." : "No photographs currently available."
        },
        tags: item.tags || []
      };
    });
  };

  const submittedEvidence = transformEvidenceData(evidenceData.evidence);
  const evidence = submittedEvidence.find(item => item.id === evidenceId);

  const handleBackToArchive = () => {
    navigate(`/database#${evidenceId}`, {
      state: { activeTab: 'evidence' }
    });
  };

  // Image gallery functions
  const getEvidenceImages = (evidenceId) => {
    // This function would typically fetch images from an API or file system
    // For now, we'll return sample images based on evidence type
    const evidence = submittedEvidence.find(item => item.id === evidenceId);
    if (!evidence) return [];
    
    const images = [];
    
    // Add sample images based on evidence type and ID
    switch (evidenceId) {
      case 'E1': // Knife with dark handle
        images.push(
          {
            src: '/images/evidence/E1/knife_overview.jpg',
            alt: 'Knife overview showing dark handle and blade',
            caption: 'Evidence overview - Dark handled knife found at crime scene'
          },
          {
            src: '/images/evidence/E1/knife_handle_detail.jpg',
            alt: 'Close-up of knife handle showing texture and material',
            caption: 'Handle detail - Close examination of handle material and texture'
          },
          {
            src: '/images/evidence/E1/knife_blade_detail.jpg',
            alt: 'Close-up of knife blade showing edge and markings',
            caption: 'Blade detail - Examination of blade edge and surface markings'
          }
        );
        break;
      case 'E2': // Blood-stained fabric
        images.push(
          {
            src: '/images/evidence/E2/fabric_overview.jpg',
            alt: 'Blood-stained fabric piece showing staining pattern',
            caption: 'Fabric overview - Blood-stained fabric with staining pattern analysis'
          },
          {
            src: '/images/evidence/E2/fabric_stain_detail.jpg',
            alt: 'Close-up of blood stain showing texture and color',
            caption: 'Stain detail - Close examination of blood stain characteristics'
          }
        );
        break;
      case 'E3': // Footprint in mud
        images.push(
          {
            src: '/images/evidence/E3/footprint_overview.jpg',
            alt: 'Footprint impression in mud showing size and pattern',
            caption: 'Footprint overview - Mud impression showing shoe size and pattern'
          },
          {
            src: '/images/evidence/E3/footprint_scale.jpg',
            alt: 'Footprint with measurement scale for analysis',
            caption: 'Scale measurement - Footprint with forensic measurement scale'
          }
        );
        break;
      default:
        // For other evidence items, show a placeholder
        images.push({
          src: '/images/evidence/placeholder.jpg',
          alt: 'Evidence photograph placeholder',
          caption: 'Evidence photograph - Image will be added during investigation'
        });
    }
    
    return images;
  };

  const openImageModal = (image) => {
    setSelectedImage(image);
    setIsImageModalOpen(true);
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Reset scroll position when component mounts (new page)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  if (!evidence) {
    return (
      <>
        <Header />
        <div className="error-message">
          <h3>Evidence Not Found</h3>
          <p>The requested evidence item could not be found.</p>
          <button className="back-btn" onClick={handleBackToArchive}>
            ← BACK TO DATABASE
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      
      <div className="evidence-detail-view">
        <div className="evidence-detail-header">
          <button 
            className="back-btn"
            onClick={handleBackToArchive}
          >
            ← BACK TO ARCHIVE
          </button>
          <div className="evidence-detail-title">
            <h3>{evidence.evidenceNumber}: {evidence.title}</h3>
            <div className={`status-badge ${evidence.status.toLowerCase().replace(/\s+/g, '-')}`}>
              {evidence.status}
            </div>
          </div>
        </div>
        
        <div className="evidence-detail-content">
          <div className="evidence-overview">
            <div className="overview-section">
              <strong>Evidence Type:</strong> {evidence.evidenceType}
            </div>
            <div className="overview-section">
              <strong>Investigation Day:</strong> {evidence.investigationDay}
            </div>
            <div className="overview-section">
              <strong>Forensic Status:</strong> {evidence.status}
            </div>
            <div className="overview-section">
              <strong>Priority Level:</strong> {evidence.priority}
            </div>
            <div className="overview-section">
              <strong>Location Found:</strong> {evidence.location}
            </div>
            <div className="overview-section">
              <strong>Relevance Score:</strong> 
              <span className="score-value">{evidence.relevanceScore}/10</span>
            </div>
            {evidence.originalData && evidence.originalData.notes && (
              <div className="overview-section">
                <strong>Investigation Notes:</strong> {evidence.originalData.notes}
              </div>
            )}
          </div>
          
          {/* Evidence Image Gallery */}
          <div className="evidence-gallery">
            {getEvidenceImages(evidence.id).map((image, index) => (
              <div key={index} className="gallery-item">
                <img 
                  src={image.src} 
                  alt={image.alt}
                  onClick={() => openImageModal(image)}
                  className="gallery-image"
                />
                <div className="image-caption">{image.caption}</div>
              </div>
            ))}
            {getEvidenceImages(evidence.id).length === 0 && (
              <div className="no-images-message">
                <p>No photographs currently available for this evidence item.</p>
                <p>Images will be added as the investigation progresses.</p>
              </div>
            )}
          </div>
          
          <div className="ai-analysis-section">
            <h4>AI ANALYSIS REPORT</h4>
            
            <div className="analysis-field">
              <h5>Evidence Review</h5>
              <p>{evidence.aiAnalysis.evidenceReview}</p>
            </div>
            
            <div className="analysis-field">
              <h5>Story Alignment</h5>
              <p>{evidence.aiAnalysis.storyAlignment}</p>
            </div>
            
            <div className="analysis-field">
              <h5>Investigative Value</h5>
              <p>{evidence.aiAnalysis.investigativeValue}</p>
            </div>
            
            {evidence.aiAnalysis.imageAnalysis && (
              <div className="analysis-field">
                <h5>Image Analysis</h5>
                <p>{evidence.aiAnalysis.imageAnalysis}</p>
              </div>
            )}
            
            <div className="analysis-field">
              <h5>Recommended Actions</h5>
              <ul className="recommended-actions-list">
                {evidence.aiAnalysis.recommendedActions.map((action, index) => (
                  <li key={index}>{action}</li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="evidence-tags">
            <h5>Evidence Tags</h5>
            <div className="tags-list">
              {evidence.tags.map((tag, index) => (
                <span key={index} className="evidence-tag">{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Image Modal */}
      {isImageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeImageModal}>×</button>
            <img 
              src={selectedImage.src} 
              alt={selectedImage.alt}
              className="modal-image"
            />
            <div className="modal-caption">{selectedImage.caption}</div>
          </div>
        </div>
      )}
    </>
  );
};

export default EvidenceDetail;
